import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Clock, ShieldCheck, Send, CheckCircle2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import BLKLogo from '../components/BLKLogo';

const SUPPORT_EMAIL = 'admin@blkpixeltech.com';

const categories = ['Bookings', 'Payments', 'Trainer Approval', 'Account Deletion', 'General'];

const faqs = [
  {
    q: 'How do I book or cancel a training session?',
    a: 'Open the RapidReps app, choose a trainer, select an available time slot, and confirm your booking. To cancel, go to “My Bookings,” tap the session, and select “Cancel.” Cancellation windows and any fees are shown before you confirm.',
  },
  {
    q: 'How do payments and refunds work?',
    a: 'Payments are processed securely in-app at the time of booking. If you cancel within the allowed window, you’ll be refunded automatically to your original payment method. For any billing question or disputed charge, email us and we’ll review it promptly.',
  },
  {
    q: 'How does trainer approval work?',
    a: 'Trainers apply through the app and submit their credentials and identification. Our team reviews each application for certification and quality before approval. Approved trainers appear in search and can accept bookings; applicants are notified by email once a decision is made.',
  },
  {
    q: 'How do I delete my account and data?',
    a: 'You can delete your account in the app under Settings → Account → Delete Account. This permanently removes your profile and associated personal data. You can also email us at admin@blkpixeltech.com with the subject “Account Deletion” and we’ll process the request for you.',
  },
];

export default function RapidRepsSupport() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', category: 'Bookings', message: '' });
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing information', description: 'Please fill in your name, email, and message.' });
      return;
    }
    const subject = encodeURIComponent(`[RapidReps Support] ${form.category} — ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nTopic: ${form.category}\n\n${form.message}`
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setDone(true);
    toast({ title: 'Opening your email app', description: 'Review and hit send to reach our team.' });
  };

  return (
    <div className="relative min-h-screen" data-testid="rapidreps-support-page">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#05050a]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="support-home-link">
            <BLKLogo />
          </Link>
          <Link
            to="/"
            className="text-[13px] text-gray-300 hover:text-white transition-colors"
            data-testid="support-back-link"
          >
            ← Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-[1080px] mx-auto px-6 lg:px-8 py-14">
        {/* Hero */}
        <div className="max-w-[720px]">
          <div className="text-[12px] font-semibold tracking-[0.3em] mb-4">
            <span className="bg-gradient-to-r from-[#a855f7] to-[#60a5fa] bg-clip-text text-transparent">
              RAPIDREPS SUPPORT
            </span>
          </div>
          <h1 className="text-white text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            We’re here to help.
          </h1>
          <p className="text-gray-400 text-[15px] leading-relaxed mt-5">
            Questions about bookings, payments, trainer approval, or your account? Reach out below and
            our team will get back to you. RapidReps is built by BLKPixel Technologies.
          </p>
        </div>

        {/* Info cards */}
        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="support-email-link"
            className="rounded-2xl p-5 bg-[#0d0d16]/70 border border-white/5 hover:border-[#a855f7]/40 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7c3aed]/30 to-[#4f46e5]/20 border border-[#a855f7]/30 flex items-center justify-center">
              <Mail className="w-4.5 h-4.5 text-[#c084fc]" />
            </div>
            <div className="text-white text-[14px] font-semibold mt-3">Email Support</div>
            <div className="text-[#c084fc] text-[13px] mt-1 break-all">{SUPPORT_EMAIL}</div>
          </a>

          <div className="rounded-2xl p-5 bg-[#0d0d16]/70 border border-white/5">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7c3aed]/30 to-[#4f46e5]/20 border border-[#a855f7]/30 flex items-center justify-center">
              <Clock className="w-4.5 h-4.5 text-[#c084fc]" />
            </div>
            <div className="text-white text-[14px] font-semibold mt-3">Response Time</div>
            <div className="text-gray-400 text-[13px] mt-1">Within 1–2 business days</div>
          </div>

          <Link
            to="/rapidreps-privacy"
            data-testid="support-privacy-link"
            className="rounded-2xl p-5 bg-[#0d0d16]/70 border border-white/5 hover:border-[#a855f7]/40 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7c3aed]/30 to-[#4f46e5]/20 border border-[#a855f7]/30 flex items-center justify-center">
              <ShieldCheck className="w-4.5 h-4.5 text-[#c084fc]" />
            </div>
            <div className="text-white text-[14px] font-semibold mt-3">Privacy Policy</div>
            <div className="text-gray-400 text-[13px] mt-1">Read how we handle your data</div>
          </Link>
        </div>

        {/* Grid: form + FAQ */}
        <div className="grid lg:grid-cols-2 gap-8 mt-14">
          {/* Contact form */}
          <div className="rounded-2xl p-6 lg:p-7 bg-gradient-to-b from-[#1a0f2e]/60 to-[#0f0817]/60 border border-white/10">
            <h2 className="text-white text-lg font-semibold">Contact Us</h2>
            <p className="text-gray-400 text-[13px] mt-1">Send us a message — it opens in your email app.</p>

            {done ? (
              <div
                className="mt-6 flex flex-col items-center text-center gap-3 py-8"
                data-testid="support-success"
              >
                <CheckCircle2 className="w-10 h-10 text-[#a855f7]" />
                <div className="text-white font-semibold">Your email is ready to send.</div>
                <p className="text-gray-400 text-[13px] max-w-[340px]">
                  We’ve opened your email app with your message pre-filled to {SUPPORT_EMAIL}. Just hit send,
                  and we’ll respond within 1–2 business days.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="text-[#c084fc] text-[13px] mt-1 hover:underline"
                  data-testid="support-send-another"
                >
                  Write another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-5 space-y-4" data-testid="support-form">
                <div>
                  <Label htmlFor="name" className="text-gray-300 text-[13px]">Name</Label>
                  <Input
                    id="name"
                    data-testid="support-name-input"
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Your name"
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300 text-[13px]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="support-email-input"
                    value={form.email}
                    onChange={update('email')}
                    placeholder="you@example.com"
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <Label htmlFor="category" className="text-gray-300 text-[13px]">Topic</Label>
                  <select
                    id="category"
                    data-testid="support-category-select"
                    value={form.category}
                    onChange={update('category')}
                    className="mt-1.5 w-full h-10 rounded-md bg-white/5 border border-white/10 text-white text-[14px] px-3 focus:outline-none focus:border-[#a855f7]/50"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c} className="bg-[#0d0d16]">{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="message" className="text-gray-300 text-[13px]">Message</Label>
                  <Textarea
                    id="message"
                    data-testid="support-message-input"
                    value={form.message}
                    onChange={update('message')}
                    placeholder="How can we help?"
                    rows={5}
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="support-submit-button"
                  className="w-full h-11 rounded-md text-[14px] font-semibold bg-gradient-to-r from-[#7c3aed] to-[#4f46e5] hover:from-[#8b45f0] hover:to-[#5b52ee] text-white border-0 shadow-[0_0_20px_rgba(124,58,237,0.35)]"
                >
                  <Send className="w-4 h-4 mr-2" /> Send Message
                </Button>
                <p className="text-gray-500 text-[11.5px] text-center">
                  Prefer email? Write to{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#c084fc] hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </form>
            )}
          </div>

          {/* FAQ */}
          <div data-testid="support-faq">
            <h2 className="text-white text-lg font-semibold">Frequently Asked Questions</h2>
            <p className="text-gray-400 text-[13px] mt-1">Quick answers to common RapidReps questions.</p>
            <Accordion type="single" collapsible className="mt-5">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-white/10"
                  data-testid={`support-faq-item-${i}`}
                >
                  <AccordionTrigger className="text-white text-[14px] text-left hover:no-underline hover:text-[#c084fc]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 text-[13px] leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-gray-500 text-[12px]">
            © {new Date().getFullYear()} BLKPixel Technologies, LLC. All rights reserved.
          </div>
          <div className="flex items-center gap-5 text-[12px]">
            <Link to="/rapidreps-privacy" className="text-gray-400 hover:text-white">
              Privacy Policy
            </Link>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-400 hover:text-white">
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
